import { useLazyQuery } from '@apollo/client'
import moment from 'moment'
import { useCallback, useMemo } from 'react'

import { COMLOG_QUERY } from '../../graphql/comlog'
import { ComLog, ComlogData } from '../../types/monitoring'
import { redactSecrets } from '../../utils/comlogRedaction'

/**
 * The PMS conversations for one property.
 *
 * Deliberately silent on failure: this panel is corroborating evidence beside
 * the comparison, not a precondition for it. A comlog service that is down must
 * not stop an operator seeing the inventory diagnostic, and an error toast here
 * would be indistinguishable from the diagnostic itself failing.
 *
 * Silent must not mean INVISIBLE, though. The failure is surfaced as `failed`
 * so the panel can say "we could not look" instead of "the PMS was never
 * asked". Those are different claims and only one of them is evidence: a failed
 * query previously produced an empty list, which rendered as the confident
 * negative "Nothing here proves the PMS was asked" on a property that had 349
 * recorded conversations at that moment.
 */
const LOOKBACK_HOURS = 6

export function usePMSEvidence() {
  const [runQuery, { data, loading, error }] = useLazyQuery<{
    comLogs: ComlogData
  }>(COMLOG_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const load = useCallback(
    (propertyCode: string) => {
      runQuery({
        variables: {
          input: {
            page: 1,
            // Enough to cover a whole ARI cycle. Haris makes one call PER
            // CHANNEL — 15 per cycle on property 10700 — so a page of 5 could
            // not even see the other channels' conversations, let alone report
            // that some of them failed. conversationMix() reads all of these.
            perPage: 40,
            From: moment().subtract(LOOKBACK_HOURS, 'hours').toISOString(),
            To: moment().toISOString(),
            EndpointName: '',
            EndpointURL: '',
            EntityFilters: [
              {
                EntityType: 'Accommodation',
                EntityCode: propertyCode,
                EntityID: ''
              }
            ]
          }
        }
      })
    },
    [runQuery]
  )

  // PMS integrations authenticate INSIDE the message body — every Mosafer
  // request carries the property's SecurityPass and the channel's AgencyPass in
  // cleartext. Masking here means no consumer of this hook can render a live
  // credential, and the panel below is styled for copy-paste into tickets.
  // Memoised on `data` because redaction is textual regex work over the FULL
  // request and response bodies, and the hook body re-runs on every render of
  // the screen — pagination clicks, column-filter toggles, the filter panel.
  // Measured at perPage 40 on a ~28KB payload: ~46ms of synchronous main-thread
  // work per incidental re-render, against ~5.6ms at the old perPage 5. The
  // column filters are deliberately instant by design, and this was quietly
  // charging them for a fetch that had not changed.
  //
  // This is the "only memoise when there is a measured need" case, and the
  // measurement is above rather than assumed.
  const logs: ComLog[] = useMemo(
    () =>
      (data?.comLogs?.comLogs ?? []).map((entry) => ({
        ...entry,
        Request: redactSecrets(entry.Request),
        Response: redactSecrets(entry.Response)
      })),
    [data]
  )

  // `failed` is deliberately distinct from `logs.length === 0`. An empty result
  // is an answer; an error is the absence of one.
  return { logs, loading, load, failed: error != null }
}
